/**
 * useRoleConfig — fetch the role config for the chat surface.
 *
 * One fetch on mount per role; result is cached in module-scope memory
 * for the page lifetime (role configs are stable, no need to re-fetch).
 *
 * Returns `null` until the fetch completes; consumers render a loading
 * fallback or default to empty arrays during the brief loading window.
 */
import { useEffect, useState } from 'react';
import type { RoleConfig, QuickStartChip } from '@rmint/core/roles/schema';

export type { RoleConfig, QuickStartChip };

const _cache = new Map<string, Promise<RoleConfig | null>>();

export async function fetchRoleConfigOnce(
  role: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RoleConfig | null> {
  let promise = _cache.get(role);
  if (promise) return promise;
  promise = fetchImpl(`/api/roles/${role}`)
    .then(async (res) => {
      if (!res.ok) return null;
      return (await res.json()) as RoleConfig;
    })
    .catch(() => null);
  _cache.set(role, promise);
  return promise;
}

export function useRoleConfig(role: string): RoleConfig | null {
  const [config, setConfig] = useState<RoleConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchRoleConfigOnce(role).then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, [role]);
  return config;
}

/** Test-only: clear the module cache between tests. */
export function _resetRoleConfigCacheForTests(): void {
  _cache.clear();
}
